import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, FileText, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DRIVER_DOC_TYPES = [
  ["license", "Driver license"], ["insurance", "Insurance"],
  ["registration", "Registration"], ["w9", "W-9"],
  ["contract", "Contract"], ["other", "Other"],
];
const TRIP_DOC_TYPES = [
  ["photo", "Photo"], ["receipt", "Receipt"], ["damage", "Damage"],
  ["signed_doc", "Signed doc"], ["other", "Other"],
];

const fmtSize = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const daysTo = (d) => Math.ceil((new Date(d + "T00:00:00") - new Date()) / 86400000);

/**
 * Reusable file/media section.
 * mode="driver_doc"       + driverId   → licenses/insurance with expiry tracking
 * mode="trip_attachment"  + bookingId  → receipts/photos on a booking
 * apiClient: axios instance WITH auth. canDelete: dispatcher only.
 */
export default function FileUploadSection({ mode, driverId, bookingId, apiClient, canDelete = false, title }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [docType, setDocType] = useState(mode === "driver_doc" ? "license" : "photo");
  const [expiry, setExpiry] = useState("");
  const inputRef = useRef(null);
  const types = mode === "driver_doc" ? DRIVER_DOC_TYPES : TRIP_DOC_TYPES;

  const reload = useCallback(async () => {
    try {
      const params = {};
      if (mode === "driver_doc" && driverId) params.driver_id = driverId;
      if (mode === "trip_attachment" && bookingId) params.booking_id = bookingId;
      const r = await apiClient.get("/files", { params });
      setFiles(r.data || []);
    } catch (_e) { /* section stays empty */ }
  }, [apiClient, mode, driverId, bookingId]);

  useEffect(() => { reload(); }, [reload]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", mode);
      fd.append("doc_type", docType);
      if (driverId) fd.append("driver_id", driverId);
      if (bookingId) fd.append("booking_id", bookingId);
      if (mode === "driver_doc" && expiry) fd.append("expiry_date", expiry);
      await apiClient.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      setExpiry("");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const remove = async (f) => {
    try {
      await apiClient.delete(`/files/${f.file_id}`);
      toast.success("Deleted");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const openFile = (f) => window.open(`${API}/files/${f.file_id}/content`, "_blank");

  return (
    <div data-testid={`files-section-${mode}`} className="border border-white/10 bg-[#0A0A0A] p-4">
      <div className="flex items-center gap-3 flex-wrap mb-3">
        <span className="text-[10px] uppercase tracking-[0.22em] text-[#71717A]">{title || (mode === "driver_doc" ? "Documents" : "Attachments")}</span>
        <select data-testid="file-doc-type" value={docType} onChange={e => setDocType(e.target.value)}
          className="bg-[#0A0A0A] border border-white/10 px-2 py-1 text-[11px] text-[#F5F2EB]">
          {types.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {mode === "driver_doc" && (
          <label className="flex items-center gap-1.5 text-[10px] text-[#71717A]">
            expires
            <input type="date" data-testid="file-expiry" value={expiry} onChange={e => setExpiry(e.target.value)}
              className="bg-[#0A0A0A] border border-white/10 px-2 py-1 text-[11px] text-[#F5F2EB]"/>
          </label>
        )}
        <button data-testid="file-upload-btn" onClick={() => inputRef.current?.click()} disabled={busy}
          className="btn-ghost !py-1 !px-3 text-[10px] ml-auto"><Upload size={12}/>{busy ? "Uploading…" : "Upload"}</button>
        <input ref={inputRef} type="file" accept="image/*,application/pdf" className="hidden"
          onChange={e => { upload(e.target.files?.[0]); e.target.value = ""; }}/>
      </div>

      {files.length === 0 && <div className="text-[11px] text-[#52525B]" data-testid="files-empty">No files yet — images or PDFs up to 10 MB.</div>}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {files.map(f => {
          const isImg = f.content_type?.startsWith("image/");
          const exp = f.expiry_date ? daysTo(f.expiry_date) : null;
          return (
            <div key={f.file_id} data-testid={`file-card-${f.file_id}`} className="border border-white/10 bg-black/30 overflow-hidden group">
              <button onClick={() => openFile(f)} className="block w-full text-left">
                {isImg ? (
                  <img src={`${API}/files/${f.file_id}/content`} alt={f.filename}
                    className="h-24 w-full object-cover" loading="lazy"/>
                ) : (
                  <div className="h-24 flex items-center justify-center text-[#71717A]"><FileText size={28}/></div>
                )}
              </button>
              <div className="p-2">
                <div className="text-[10px] text-[#F5F2EB] truncate" title={f.filename}>{f.filename}</div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[9px] uppercase tracking-[0.12em] text-[#71717A]">{f.doc_type} · {fmtSize(f.size)}</span>
                  {canDelete && (
                    <button data-testid={`file-delete-${f.file_id}`} onClick={() => remove(f)}
                      className="ml-auto text-[#52525B] hover:text-red-300"><Trash2 size={11}/></button>
                  )}
                </div>
                {exp !== null && (
                  <div className={`flex items-center gap-1 mt-1 text-[9px] uppercase tracking-[0.12em] ${
                    exp < 0 ? "text-red-300" : exp <= 45 ? "text-amber-300" : "text-[#71717A]"}`}>
                    {exp <= 45 && <AlertTriangle size={9}/>}
                    {exp < 0 ? `expired ${-exp}d ago` : `expires in ${exp}d`} · {f.expiry_date}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
